import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Save, RotateCcw, Lock } from "lucide-react";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function Branding() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("cbr.meta_title"));
  const [state, setState] = useState(null);
  const [logo, setLogo] = useState("");
  const [color, setColor] = useState("");
  const [footer, setFooter] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/branding");
      setState(data);
      setLogo(data?.branding?.logo_url || "");
      setColor(data?.branding?.brand_color || "");
      setFooter(data?.branding?.footer_text || "");
    } catch { setState({ is_pro: false, branding: {} }); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const save = async () => {
    if (logo && !/^https?:\/\//i.test(logo)) { toast.error(t("cbr.err_logo")); return; }
    if (color && !HEX_RE.test(color)) { toast.error(t("cbr.err_color")); return; }
    setSaving(true);
    try {
      await api.put("/coach/branding", { logo_url: logo || null, brand_color: color || null, footer_text: footer || null });
      toast.success(t("cbr.saved"));
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(t("cbr.reset_confirm"))) return;
    setSaving(true);
    try { await api.delete("/coach/branding"); toast.success(t("cbr.reset_done")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const isPro = state?.is_pro;

  return (
    <div data-testid="coach-branding-page">
      <Seo title={t("cbr.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cbr-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("cbr.eyebrow")} title={t("cbr.title")} subtitle={t("cbr.subtitle")} />

      {state === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : !isPro ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="cbr-locked">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Lock className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("cbr.locked_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cbr.locked_sub")}</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cbr.f_logo")}</label>
            <Input value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…/logo.png" data-testid="cbr-logo" />
            <p className="mt-1 text-xs text-muted-foreground">{t("cbr.f_logo_hint")}</p>
            {logo && /^https?:\/\//i.test(logo) && <img src={logo} alt="" className="mt-2 h-14 rounded-lg border border-border bg-white object-contain p-1" />}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cbr.f_color")}</label>
            <div className="flex items-center gap-2">
              <span className="h-10 w-10 shrink-0 rounded-lg border border-border" style={{ background: HEX_RE.test(color) ? color : "transparent" }} />
              <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#6B8E6E" className="max-w-[160px]" data-testid="cbr-color" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cbr.f_footer")}</label>
            <Textarea className="min-h-[70px]" maxLength={200} value={footer} onChange={(e) => setFooter(e.target.value)} placeholder={t("cbr.f_footer_ph")} data-testid="cbr-footer" />
            <p className="mt-1 text-right text-[11px] text-muted-foreground">{footer.length}/200</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button className="gap-2 rounded-full" onClick={save} disabled={saving} data-testid="cbr-save"><Save className="h-4 w-4" /> {saving ? t("common.saving") : t("cbr.save")}</Button>
            <Button variant="outline" className="gap-2 rounded-full" onClick={reset} disabled={saving} data-testid="cbr-reset"><RotateCcw className="h-4 w-4" /> {t("cbr.reset")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
